/* ELIO shipping qualification state.
   ONE source of truth for "does this cart get free U.S. shipping".

   Why this exists: this theme dispatches no cart events, so every ELIO component
   independently patches fetch/XHR and re-reads /cart.js. Six of them already do.
   The meter does NOT add a seventh — it subscribes here instead.

   Qualifying value = cart.total_price from /cart.js.
   That is Shopify's own post-discount, pre-tax, pre-shipping figure, so the pair offer and
   any live discount code are already baked in. No pricing math is recreated here.

   Emits: document -> 'elio:ship:state' with detail {
     ready, qualifies, qualifyingCents, remainingCents, thresholdCents,
     marketIsUS, unitPrices
   }
*/
(function () {
  'use strict';

  // Threshold is authored in Settings -> Theme settings -> "ELIO - Free shipping"
  // and rendered onto the meter element. Never hardcode it here.
  function readThreshold() {
    var el = document.querySelector('[data-elio-threshold-cents]');
    var v = el ? Number(el.getAttribute('data-elio-threshold-cents')) : 0;
    return (v > 0) ? v : 2500;
  }
  var THRESHOLD_CENTS = 2500;

  var state = {
    ready: false,            // false until the first real read lands
    qualifies: false,
    qualifyingCents: 0,
    remainingCents: THRESHOLD_CENTS,
    thresholdCents: THRESHOLD_CENTS,
    marketIsUS: true,
    unitPrices: [],
    unitItems: []
  };

  function marketIsUS() {
    var el = document.querySelector('[data-elio-market]');
    if (el) return (el.getAttribute('data-elio-market') || '').toUpperCase() === 'US';
    // Fall back to any element already carrying the shop country (PDP + cart page do).
    var c = document.querySelector('[data-shop-country]');
    if (c) return (c.getAttribute('data-shop-country') || '').toUpperCase() === 'US';
    return true;
  }

  /* Eligible briefs only. The pair rule applies to briefs, so counting raw
     cart units would miscount the moment a non-brief exists: 2 briefs plus
     one other item would read as odd and the projection would disagree with
     the discount Shopify actually applies. Mirrors isEligibleBrief() in
     elio-checkout-nudge.js so the two never diverge. */
  var ELIO_COLOR_KEYS = ['tan', 'nude', 'espresso', 'brown', 'pine', 'green',
                         'navy', 'blue', 'black', 'crimson', 'red'];

  function isEligibleBrief(item) {
    if (!item) return false;
    var text = [item.product_title, item.title, item.handle]
      .filter(Boolean).join(' ').toLowerCase();

    // Pair eligibility follows the brief itself, not fabric family or fulfillment state.
    if (text.indexOf('brief') === -1) return false;
    for (var i = 0; i < ELIO_COLOR_KEYS.length; i++) {
      if (text.indexOf(ELIO_COLOR_KEYS[i]) !== -1) return true;
    }
    return false;
  }

  // Expand ELIGIBLE cart lines into individual undiscounted units, preserving
  // fabric family so Original Cotton and Bamboo Cotton never cross-complete an offer.
  function expandUnitItems(items) {
    var out = [];
    (items || []).filter(isEligibleBrief).forEach(function (item) {
      var qty = Math.max(1, Number((item && item.quantity) || 1));
      var unit = Number(
        (item && item.original_price != null) ? item.original_price : ((item && item.price) || 0)
      );
      var family = (window.ElioOffer && ElioOffer.familyKey) ? ElioOffer.familyKey(item) : (/bamboo/i.test([item.product_title,item.title,item.handle].filter(Boolean).join(' ')) ? 'bamboo' : 'original');
      if (unit > 0) for (var i = 0; i < qty; i++) out.push({ price: unit, family: family });
    });
    return out;
  }

  /* Theme-side projection only. The global offer engine owns Bundle vs BOGO
     math; Shopify's returned totals remain authoritative after mutation. */
  function offerDiscountForItems(unitItems) {
    if (window.ElioOffer && typeof ElioOffer.discountForItems === 'function') {
      return ElioOffer.discountForItems(unitItems || []);
    }
    var count = (unitItems || []).length;
    return Math.floor(count / 2) * 1000;
  }

  // Backward-compatible public helper. Numeric arrays are treated as Original Cotton.
  function pairDiscountForUnits(unitPrices, family) {
    var fam = family || 'original';
    var items = (unitPrices || []).map(function (p) { return { price: Number(p || 0), family: fam }; });
    return offerDiscountForItems(items);
  }

  function computeFrom(cents) {
    var v = Math.max(0, Number(cents || 0));
    state.qualifyingCents = v;
    state.qualifies = v >= THRESHOLD_CENTS;
    state.remainingCents = Math.max(0, THRESHOLD_CENTS - v);
  }

  function emit() {
    document.dispatchEvent(new CustomEvent('elio:ship:state', {
      detail: {
        ready: state.ready,
        qualifies: state.qualifies,
        qualifyingCents: state.qualifyingCents,
        remainingCents: state.remainingCents,
        thresholdCents: state.thresholdCents,
        marketIsUS: state.marketIsUS,
        unitPrices: state.unitPrices.slice(),
        unitItems: state.unitItems.slice()
      }
    }));
  }

  /* Every cart mutation MUST produce a fresh read.

     The previous version deduped on an in-flight request and returned the old
     promise. Deleting items in quick succession therefore dropped reads: the
     second delete arrived while the first fetch was open, got silently skipped,
     and the meter kept showing whatever the first response said. That is how a
     $24 cart kept claiming free shipping.

     Instead: always fetch, and use a sequence number so a slow earlier response
     can never overwrite a newer one. */
  var seq = 0;
  function read() {
    var mine = ++seq;
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return null;
        if (mine !== seq) return cart;   // a newer read already landed; discard
        state.unitItems = expandUnitItems(cart.items);
        state.unitPrices = state.unitItems.map(function (x) { return x.price; });
        computeFrom(cart.total_price);
        state.ready = true;
        emit();
        return cart;
      })
      .catch(function () { return null; });
  }

  function markStale() {
    // Not "still qualified" and not "no longer qualified" — unknown. Meters
    // fall back to the neutral threshold statement, which claims nothing about
    // this cart, until the authoritative read lands.
    state.ready = false;
    emit();
  }

  var pending = null;
  function reread() {
    markStale();
    clearTimeout(pending);
    pending = setTimeout(read, 90);
    // Belt and braces: a trailing read after the theme has finished its own
    // re-render, so the last state in a rapid burst is always the true one.
    setTimeout(read, 450);
  }

  /* Projection for the PDP: what the cart becomes if this selection is added.
     selectionUnitPrices is an array of undiscounted unit prices (one entry per
     brief being added — two for the pair). Returns the same shape as state. */
  function project(selectionUnitPrices, family) {
    var fam = family || 'original';
    var sel = (selectionUnitPrices || []).map(function (entry) {
      if (entry && typeof entry === 'object') return { price: Number(entry.price || 0), family: entry.family || fam };
      return { price: Number(entry || 0), family: fam };
    }).filter(function (entry) { return entry.price > 0; });

    // Cart already qualifies: PDP selection is irrelevant. Spec State C.
    if (state.qualifies) {
      return {
        ready: state.ready,
        qualifies: true,
        qualifyingCents: state.qualifyingCents,
        remainingCents: 0,
        thresholdCents: THRESHOLD_CENTS,
        cartAlreadyQualified: true,
        addedBySelection: false
      };
    }

    var current = state.unitItems.slice();
    var combined = current.concat(sel);

    var existingDiscount = offerDiscountForItems(current);
    var projectedDiscount = offerDiscountForItems(combined);
    var incrementalDiscount = Math.max(0, projectedDiscount - existingDiscount);

    var selGross = sel.reduce(function (a, b) { return a + Number(b.price || 0); }, 0);
    var projectedCents = state.qualifyingCents + selGross - incrementalDiscount;

    return {
      ready: state.ready,
      qualifies: projectedCents >= THRESHOLD_CENTS,
      qualifyingCents: projectedCents,
      remainingCents: Math.max(0, THRESHOLD_CENTS - projectedCents),
      thresholdCents: THRESHOLD_CENTS,
      cartAlreadyQualified: false,
      addedBySelection: sel.length > 0
    };
  }

  // Reuse the established change-detection pattern rather than inventing one.
  // Guarded so this file and elio-cart-push can both patch without stacking.
  if (window.fetch && !window.__ELIO_SHIP_FETCH__) {
    window.__ELIO_SHIP_FETCH__ = true;
    var _fetch = window.fetch;
    window.fetch = function (input) {
      var url = (typeof input === 'string') ? input : ((input && input.url) || '');
      var p = _fetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/.test(url)) p.then(reread, function () {});
      return p;
    };
  }
  if (window.XMLHttpRequest && !window.__ELIO_SHIP_XHR__) {
    window.__ELIO_SHIP_XHR__ = true;
    var _open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (m, url) {
      if (/\/cart\/(add|change|update|clear)/.test(String(url || ''))) {
        this.addEventListener('load', reread);
      }
      return _open.apply(this, arguments);
    };
  }

  ['cart:update', 'cart:updated', 'cart:change', 'cart:refresh', 'ajaxCart.afterCartLoad']
    .forEach(function (e) { document.addEventListener(e, reread); });

  // Back/forward cache and returning tabs must not show stale qualification.
  window.addEventListener('pageshow', function () { read(); });
  document.addEventListener('visibilitychange', function () { if (!document.hidden) read(); });

  var badge = document.getElementById('headerCartStatus');
  if (badge && window.MutationObserver) {
    new MutationObserver(reread).observe(badge, {
      childList: true, subtree: true, characterData: true, attributes: true
    });
  }

  window.ElioShipState = {
    THRESHOLD_CENTS: THRESHOLD_CENTS,
    get: function () { return Object.assign({}, state); },
    project: project,
    pairDiscountForUnits: pairDiscountForUnits,
    offerDiscountForItems: offerDiscountForItems,
    refresh: read,
    subscribe: function (fn) {
      document.addEventListener('elio:ship:state', function (e) { fn(e.detail); });
      if (state.ready) fn(Object.assign({}, state));
    }
  };

  function boot() {
    THRESHOLD_CENTS = readThreshold();
    state.thresholdCents = THRESHOLD_CENTS;
    state.remainingCents = THRESHOLD_CENTS;
    state.marketIsUS = marketIsUS();
    read();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
